import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Button,
  Stack,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Select,
  Option,
  Box,
  Alert,
} from '@mui/joy';
import type { AutomationTrigger, AutomationAction, AutomationModifier } from '../../types/automations';
import type { Tag, User } from '../../types';
import type { Form } from '../../lib/apiMarketing';
import { FloppyDisk as Save } from '@phosphor-icons/react';
import { apiClient, type ApiResponse } from '../../lib/api';
import { AutomationsAPI } from '../../lib/apiAutomations';
import type { StageOptionsResponse } from '../../types/automations';

type WorkflowItemData = AutomationTrigger | AutomationAction | AutomationModifier;

interface WorkflowItemConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: Record<string, unknown>) => void;
  item: {
    type: 'trigger' | 'action' | 'modifier';
    data: WorkflowItemData;
  } | null;
}

const WorkflowItemConfigModal: React.FC<WorkflowItemConfigModalProps> = ({
  open,
  onClose,
  onSave,
  item,
}) => {
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [tags, setTags] = useState<Tag[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [optionsError, setOptionsError] = useState('');
  const [stages, setStages] = useState<StageOptionsResponse>({ opportunity_statuses: [], job_statuses: [] });

  useEffect(() => {
    if (item) {
      setConfig(item.data.config || {});
    }
  }, [open, item?.type, item?.data.id]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function allPages<T>(path: string): Promise<T[]> {
      const rows: T[] = [];
      let next: string | null = path;
      const visited = new Set<string>();
      while (next && !visited.has(next)) {
        visited.add(next);
        const response: ApiResponse<T[] | { results: T[]; next?: string | null }> = await apiClient.get(next);
        if (Array.isArray(response.data)) return [...rows, ...response.data];
        rows.push(...response.data.results);
        next = response.data.next ? new URL(response.data.next, window.location.origin).pathname + new URL(response.data.next, window.location.origin).search : null;
      }
      return rows;
    }
    Promise.allSettled([allPages<Tag>('/api/tags/'), allPages<User>('/api/users/'), allPages<Form>('/api/marketing/forms/'), AutomationsAPI.getStageOptions()]).then(([tagsResult, usersResult, formsResult, stagesResult]) => {
      if (cancelled) return;
      if (tagsResult.status === 'fulfilled') setTags(tagsResult.value);
      if (usersResult.status === 'fulfilled') setUsers(usersResult.value);
      if (formsResult.status === 'fulfilled') setForms(formsResult.value);
      if (stagesResult.status === 'fulfilled') setStages(stagesResult.value);
      setOptionsError([tagsResult, usersResult, formsResult, stagesResult].some(result => result.status === 'rejected') ? 'Some options could not be loaded. Reopen this dialog to retry.' : '');
    });
    return () => { cancelled = true; };
  }, [open]);

  const handleSave = () => {
    const effective = { ...config };
    if (item?.type === 'modifier') {
      const kind = (item.data as AutomationModifier).modifier_type;
      if (kind === 'wait') Object.assign(effective, { duration_value: num('duration_value', 1), duration_unit: str('duration_unit', 'hours') });
      if (kind === 'if_else') Object.assign(effective, { condition_type: str('condition_type', 'field'), operator: str('operator', 'equals') });
    }
    if (item?.type === 'trigger' && (item.data as AutomationTrigger).trigger_type === 'payment_overdue') effective.days_overdue = num('days_overdue', 7);
    if (item?.type === 'action') {
      const kind = (item.data as AutomationAction).action_type;
      if (kind === 'send_email' && !effective.to) effective.to = ['contact'];
      if (kind === 'create_notification' && !effective.user_targets) effective.user_targets = ['owner'];
    }
    onSave(effective);
    onClose();
  };

  const updateConfig = (key: string, value: unknown) => {
    setConfig({ ...config, [key]: value });
  };

  // Safe getters for form values - config values are unknown, coerce to expected types for Joy UI
  const str = (key: string, def = ''): string => {
    const v = config[key];
    return v != null && (typeof v === 'string' || typeof v === 'number') ? String(v) : def;
  };
  const num = (key: string, def: number): number => {
    const v = config[key];
    return typeof v === 'number' && !isNaN(v) ? v : def;
  };
  const numOrStr = (key: string, def: number | ''): number | string => {
    const v = config[key];
    if (typeof v === 'number' && !isNaN(v)) return v;
    if (typeof v === 'string') return v;
    return def;
  };
  const strArr = (key: string): string[] => {
    const v = config[key];
    return Array.isArray(v) ? v.map((x) => String(x)) : [];
  };
  const numArr = (key: string): number[] => {
    const v = config[key];
    return Array.isArray(v)
      ? v
          .map((x) => (typeof x === 'number' ? x : Number(x)))
          .filter((n): n is number => !isNaN(n))
      : [];
  };
  const idArr = (key: string): (string | number)[] => {
    const v = config[key];
    return Array.isArray(v)
      ? v.filter((x): x is string | number => typeof x === 'string' || (typeof x === 'number' && !isNaN(x)))
      : [];
  };

  if (!item) return null;

  const getTitle = (): string => {
    if (item.type === 'trigger') {
      const trigger = item.data as AutomationTrigger;
      return trigger.trigger_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    } else if (item.type === 'action') {
      const action = item.data as AutomationAction;
      return action.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    } else {
      const modifier = item.data as AutomationModifier;
      return modifier.modifier_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const renderTriggerConfig = (trigger: AutomationTrigger) => {
    switch (trigger.trigger_type) {
      case 'opportunity_tag':
      case 'job_tag':
      case 'contact_tag':
        return (
          <FormControl>
            <FormLabel>Tags</FormLabel>
            <Select
              multiple
              value={numArr('tag_ids')}
              onChange={(_, value) => updateConfig('tag_ids', value)}
              placeholder="Select tags..."
            >
              {tags.map((tag) => (
                <Option key={tag.id} value={tag.id}>
                  {tag.name}
                </Option>
              ))}
            </Select>
          </FormControl>
        );

      case 'payment_overdue':
        return (
          <FormControl>
            <FormLabel>Days Overdue</FormLabel>
            <Input
              type="number"
              value={num('days_overdue', 7)}
              onChange={(e) => updateConfig('days_overdue', e.target.value ? parseInt(e.target.value) : null)}
              placeholder="7"
            />
          </FormControl>
        );

      case 'payment_received':
        return (
          <FormControl>
            <FormLabel>Minimum Amount (optional)</FormLabel>
            <Input
              type="number"
              value={numOrStr('minimum_amount', '')}
              onChange={(e) => updateConfig('minimum_amount', e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="0"
            />
          </FormControl>
        );

      case 'opportunity_stage_changed':
      case 'opportunity_stage_stalled':
      case 'job_stage_changed':
      case 'job_stage_stalled':
        return <>
          <FormControl>
            <FormLabel>Pipeline stages (empty means any stage)</FormLabel>
            <Select multiple value={numArr('status_ids')} onChange={(_, value) => setConfig({ ...config, stages: [], status_ids: value })} placeholder="Any stage">
              {(trigger.trigger_type.startsWith('job') ? stages.job_statuses : stages.opportunity_statuses).map(stage =>
                <Option key={stage.id} value={stage.id}>{stage.board_name}: {stage.name}</Option>)}
            </Select>
          </FormControl>
          {strArr('stages').length > 0 && <Typography level="body-sm">Existing legacy filter: {strArr('stages').join(', ')}. Selecting a pipeline stage replaces it.</Typography>}
          {trigger.trigger_type.endsWith('stalled') && <FormControl>
            <FormLabel>Stall hours</FormLabel>
            <Input type="number" value={num('stall_hours', 24)} onChange={e => updateConfig('stall_hours', Number(e.target.value))} slotProps={{ input: { min: 1 } }} />
          </FormControl>}
        </>;

      case 'form_submitted':
        return (
          <FormControl>
            <FormLabel>Forms (empty means any form)</FormLabel>
            <Select
              multiple
              value={idArr('form_ids')}
              onChange={(_, value) => updateConfig('form_ids', value)}
              placeholder="Select one or more forms..."
              renderValue={(selected) => {
                // Handle the case where selected might be Option components
                const selectedValues = Array.isArray(selected) ? selected : [];
                
                if (selectedValues.length === 0) {
                  return 'Select one or more forms...';
                }
                
                // Find the corresponding form names
                const selectedForms = forms.filter(f => 
                  selectedValues.some(v => {
                    // Handle both direct values and Option component values
                    const valueToCheck = typeof v === 'object' && v !== null && 'props' in v &&
                                        v.props && typeof v.props === 'object' && 'value' in v.props
                                        ? (v.props as { value: string }).value : v;
                    return f.id === valueToCheck;
                  })
                );
                
                if (selectedForms.length === 0 && selectedValues.length > 0) {
                  // Fallback if we can't find the forms (might be IDs)
                  return `${selectedValues.length} Form${selectedValues.length > 1 ? 's' : ''} Selected`;
                }
                
                if (selectedForms.length === 1) {
                  return selectedForms[0].name;
                }
                
                return `${selectedForms.length} Forms Selected`;
              }}
            >
              {forms.length > 0 ? (
                forms.map((form) => (
                  <Option key={form.id} value={form.id}>
                    {form.name}
                  </Option>
                ))
              ) : (
                <Typography level="body-sm" sx={{ p: 1, fontStyle: 'italic' }}>
                  No forms available
                </Typography>
              )}
            </Select>
            <Typography level="body-xs" sx={{ mt: 1, color: 'text.secondary' }}>
              This automation will trigger when any of the selected forms are submitted
              {forms.length === 0 && ' (Create forms in Marketing > Forms first)'}
            </Typography>
          </FormControl>
        );

      default:
        return (
          <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No configuration needed for this trigger type.
          </Typography>
        );
    }
  };

  const renderActionConfig = (action: AutomationAction) => {
    switch (action.action_type) {
      case 'send_email':
        return (
          <>
            <FormControl required>
              <FormLabel>Recipients</FormLabel>
              <Select
                multiple
                value={strArr('to').length ? strArr('to') : ['contact']}
                onChange={(_, value) => updateConfig('to', value)}
              >
                <Option value="contact">Contact</Option>
                <Option value="owner">Primary rep (owner)</Option>
                <Option value="all_reps">All reps on the deal</Option>
                <Option value="assignee">Assigned To</Option>
                <Option value="static">Specific Email(s)</Option>
              </Select>
            </FormControl>
            <FormControl><FormLabel>Maximum sending frequency per record</FormLabel>
              <Select value={str('send_frequency', 'daily')} onChange={(_, value) => updateConfig('send_frequency', value)}>
                <Option value="daily">Once per day</Option><Option value="weekly">Once per week</Option><Option value="monthly">Once per month</Option>
              </Select>
            </FormControl>
            {strArr('to').includes('static') && (
              <FormControl>
                <FormLabel>Email Addresses (comma-separated)</FormLabel>
                <Input
                  value={strArr('static_emails').join(',')}
                  onChange={(e) => updateConfig('static_emails', e.target.value.split(',').map(s => s.trim()))}
                  placeholder="email1@example.com, email2@example.com"
                />
              </FormControl>
            )}
            <FormControl required>
              <FormLabel>Subject</FormLabel>
              <Input
                value={str('subject')}
                onChange={(e) => updateConfig('subject', e.target.value)}
                placeholder="Email subject"
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Body</FormLabel>
              <Textarea
                value={str('body')}
                onChange={(e) => updateConfig('body', e.target.value)}
                placeholder="Email body text"
                minRows={4}
              />
            </FormControl>
          </>
        );

      case 'create_notification':
        return (
          <>
            <FormControl required>
              <FormLabel>Title</FormLabel>
              <Input
                value={str('title')}
                onChange={(e) => updateConfig('title', e.target.value)}
                placeholder="Notification title"
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Body</FormLabel>
              <Textarea
                value={str('body')}
                onChange={(e) => updateConfig('body', e.target.value)}
                placeholder="Notification message"
                minRows={3}
              />
            </FormControl>
            <FormControl>
              <FormLabel>User Targets</FormLabel>
              <Select
                multiple
                value={strArr('user_targets').length ? strArr('user_targets') : ['owner']}
                onChange={(_, value) => updateConfig('user_targets', value)}
              >
                <Option value="owner">Primary rep (owner)</Option>
                <Option value="all_reps">All reps on the deal</Option>
                <Option value="assignee">Assigned To</Option>
                <Option value="static">Specific Users</Option>
                <Option value="all_admins">Organization admins</Option>
              </Select>
            </FormControl>
            {strArr('user_targets').includes('static') && <FormControl><FormLabel>Recipients</FormLabel>
              <Select multiple value={strArr('static_user_ids')} onChange={(_, value) => updateConfig('static_user_ids', value)}>
                {users.map(user => <Option key={user.id} value={String(user.id)}>{user.first_name} {user.last_name} ({user.email})</Option>)}
              </Select>
            </FormControl>}
          </>
        );

      case 'create_task':
        return (
          <>
            <FormControl required>
              <FormLabel>Task Title</FormLabel>
              <Input
                value={str('title')}
                onChange={(e) => updateConfig('title', e.target.value)}
                placeholder="Task title"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={str('description')}
                onChange={(e) => updateConfig('description', e.target.value)}
                placeholder="Task description"
                minRows={2}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Due in (days)</FormLabel>
              <Input
                type="number"
                value={numOrStr('due_days', '')}
                onChange={(e) => updateConfig('due_days', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="3"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Assign To</FormLabel>
              <Select
                value={str('assigned_to_type', 'unassigned')}
                onChange={(_, value) => updateConfig('assigned_to_type', value)}
              >
                <Option value="unassigned">Leave unassigned</Option>
                <Option value="owner">Primary rep (owner)</Option>
                <Option value="all_reps">All reps on the deal</Option>
                <Option value="assigned_to">Currently assigned to</Option>
                <Option value="specific_user">Specific user</Option>
              </Select>
              {str('assigned_to_type') === 'all_reps' && (
                <FormHelperText>One task for the primary rep; the other reps on the deal are notified.</FormHelperText>
              )}
            </FormControl>
            {str('assigned_to_type') === 'specific_user' && (
              <FormControl required>
                <FormLabel>Select User</FormLabel>
                <Select
                  value={str('assigned_to_user_id')}
                  onChange={(_, value) => updateConfig('assigned_to_user_id', value)}
                  placeholder="Select a user..."
                >
                  {users.map((user) => (
                    <Option key={user.id} value={String(user.id)}>
                      {user.first_name} {user.last_name} ({user.email})
                    </Option>
                  ))}
                </Select>
              </FormControl>
            )}
          </>
        );

      case 'send_sms':
        return (
          <>
            <FormControl required>
              <FormLabel>Send To</FormLabel>
              <Select
                value={str('to', 'contact')}
                onChange={(_, value) => updateConfig('to', value)}
              >
                <Option value="contact">Contact</Option>
                <Option value="owner">Primary rep (owner)</Option>
                <Option value="all_reps">All reps on the deal</Option>
                <Option value="assigned_to">Assigned To</Option>
                <Option value="static">Specific Number</Option>
              </Select>
            </FormControl>
            {str('to') === 'static' && (
              <FormControl required>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  value={str('phone')}
                  onChange={(e) => updateConfig('phone', e.target.value)}
                  placeholder="+1234567890"
                />
              </FormControl>
            )}
            <FormControl required>
              <FormLabel>Message</FormLabel>
              <Textarea
                value={str('message')}
                onChange={(e) => updateConfig('message', e.target.value)}
                placeholder="SMS message"
                minRows={3}
              />
            </FormControl>
          </>
        );

      case 'add_tag':
      case 'remove_tag':
        return (
          <FormControl required>
            <FormLabel>Tags</FormLabel>
            <Select
              multiple
              value={numArr('tag_ids')}
              onChange={(_, value) => updateConfig('tag_ids', value)}
              placeholder="Select tags..."
            >
              {tags.map((tag) => (
                <Option key={tag.id} value={tag.id}>
                  {tag.name}
                </Option>
              ))}
            </Select>
          </FormControl>
        );

      case 'add_note':
        return (
          <FormControl required>
            <FormLabel>Note Content</FormLabel>
            <Textarea
              value={str('content')}
              onChange={(e) => updateConfig('content', e.target.value)}
              placeholder="Note text"
              minRows={3}
            />
          </FormControl>
        );

      case 'assign_to_user':
        return (
          <FormControl required>
            <FormLabel>User</FormLabel>
            <Select
              value={str('user_id')}
              onChange={(_, value) => updateConfig('user_id', value)}
              placeholder="Select user..."
            >
              {users.map((user) => (
                <Option key={user.id} value={String(user.id)}>
                  {user.first_name} {user.last_name} ({user.email})
                </Option>
              ))}
            </Select>
          </FormControl>
        );

      case 'modify_opportunity_confidence':
        return (
          <>
            <FormControl required>
              <FormLabel>Operation</FormLabel>
              <Select
                value={str('operation', 'set')}
                onChange={(_, value) => updateConfig('operation', value)}
              >
                <Option value="increase">Increase</Option>
                <Option value="decrease">Decrease</Option>
                <Option value="set">Set To</Option>
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Value</FormLabel>
              <Input
                type="number"
                value={num('value', 0)}
                onChange={(e) => updateConfig('value', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="10"
              />
            </FormControl>
          </>
        );

      case 'create_contact':
        return (
          <>
            <FormControl required>
              <FormLabel>First Name</FormLabel>
              <Input
                value={str('first_name')}
                onChange={(e) => updateConfig('first_name', e.target.value)}
                placeholder="John"
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Last Name</FormLabel>
              <Input
                value={str('last_name')}
                onChange={(e) => updateConfig('last_name', e.target.value)}
                placeholder="Doe"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                value={str('email')}
                onChange={(e) => updateConfig('email', e.target.value)}
                placeholder="john@example.com"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                value={str('phone')}
                onChange={(e) => updateConfig('phone', e.target.value)}
                placeholder="+1234567890"
              />
            </FormControl>
          </>
        );

      case 'create_opportunity':
        return (
          <>
            <FormControl required>
              <FormLabel>Opportunity Name</FormLabel>
              <Input
                value={str('name')}
                onChange={(e) => updateConfig('name', e.target.value)}
                placeholder="New Opportunity"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Pipeline stage</FormLabel>
              <Select value={config.status_id == null ? null : Number(config.status_id)} onChange={(_, value) => updateConfig('status_id', value)} placeholder="Default lead stage">
                {stages.opportunity_statuses.map(stage => <Option key={stage.id} value={stage.id}>{stage.board_name}: {stage.name}</Option>)}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Contact ID (optional)</FormLabel>
              <Input
                type="number"
                value={numOrStr('contact_id', '')}
                onChange={(e) => updateConfig('contact_id', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Defaults to the triggering contact"
              />
            </FormControl>
            <FormControl>
              <FormLabel>Owner (optional)</FormLabel>
              <Select
                value={str('owner_id')}
                onChange={(_, value) => updateConfig('owner_id', value ? parseInt(value) : null)}
                placeholder="Select an owner..."
              >
                <Option value="">Unassigned</Option>
                {users.map((user) => (
                  <Option key={user.id} value={String(user.id)}>
                    {user.first_name} {user.last_name} ({user.email})
                  </Option>
                ))}
              </Select>
            </FormControl>
          </>
        );

      case 'send_review_request':
        return (
          <>
            <Typography level="body-sm">Uses the active platform and review link configured in Marketing &gt; Reviews.</Typography>
            <FormControl><FormLabel>Send after (days)</FormLabel><Input type="number" value={num('delay_days', 0)} onChange={e => updateConfig('delay_days', Number(e.target.value))} slotProps={{ input: { min: 0 } }} /></FormControl>
            <FormControl>
              <FormLabel>Platform</FormLabel>
              <Select
                value={str('platform', 'google')}
                onChange={(_, value) => updateConfig('platform', value)}
              >
                <Option value="google">Google</Option>
                <Option value="yelp">Yelp</Option>
                <Option value="facebook">Facebook</Option>
                <Option value="bbb">Better Business Bureau</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Custom Message (optional)</FormLabel>
              <Textarea
                value={str('message')}
                onChange={(e) => updateConfig('message', e.target.value)}
                placeholder="We'd love your feedback!"
                minRows={2}
              />
            </FormControl>
          </>
        );

      default:
        return (
          <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No configuration needed for this action type.
          </Typography>
        );
    }
  };

  const renderModifierConfig = (modifier: AutomationModifier) => {
    switch (modifier.modifier_type) {
      case 'wait':
        return (
          <>
            <FormControl required>
              <FormLabel>Duration Value</FormLabel>
              <Input
                type="number"
                value={num('duration_value', 1)}
                onChange={(e) => updateConfig('duration_value', e.target.value ? parseInt(e.target.value) : null)}
                placeholder="1"
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Duration Unit</FormLabel>
              <Select
                value={str('duration_unit', 'hours')}
                onChange={(_, value) => updateConfig('duration_unit', value)}
              >
                <Option value="minutes">Minutes</Option>
                <Option value="hours">Hours</Option>
                <Option value="days">Days</Option>
                <Option value="weeks">Weeks</Option>
              </Select>
            </FormControl>
          </>
        );

      case 'if_else':
        return (
          <>
            <FormControl required>
              <FormLabel>Condition Type</FormLabel>
              <Select
                value={str('condition_type', 'field')}
                onChange={(_, value) => updateConfig('condition_type', value)}
              >
                <Option value="field">Field Value</Option>
                <Option value="tag">Has Tag</Option>
                <Option value="stage">Stage Is</Option>
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Field</FormLabel>
              <Input
                value={str('field')}
                onChange={(e) => updateConfig('field', e.target.value)}
                placeholder="e.g., confidence, status, stage"
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Operator</FormLabel>
              <Select
                value={str('operator', 'equals')}
                onChange={(_, value) => updateConfig('operator', value)}
              >
                <Option value="equals">Equals</Option>
                <Option value="not_equals">Not Equals</Option>
                <Option value="greater_than">Greater Than</Option>
                <Option value="less_than">Less Than</Option>
                <Option value="contains">Contains</Option>
              </Select>
            </FormControl>
            <FormControl required>
              <FormLabel>Value</FormLabel>
              <Input
                value={str('value')}
                onChange={(e) => updateConfig('value', e.target.value)}
                placeholder="Value to compare"
              />
            </FormControl>
          </>
        );

      case 'end':
        return (
          <Typography level="body-sm" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            This modifier ends the workflow. No configuration needed.
          </Typography>
        );

      default:
        return null;
    }
  };

  const renderConfigForm = () => {
    if (item.type === 'trigger') {
      return renderTriggerConfig(item.data as AutomationTrigger);
    } else if (item.type === 'action') {
      return <>
        <FormControl><FormLabel>Use data from</FormLabel>
          <Select value={str('target', 'trigger_record')} onChange={(_, value) => updateConfig('target', value)}>
            <Option value="trigger_record">Triggering record</Option>
            <Option value="contact">Related contact</Option>
            <Option value="opportunity">Related opportunity</Option>
            <Option value="job">Related job</Option>
          </Select>
          <Typography level="body-xs">The related record must exist. A missing relationship pauses the run with an explanation.</Typography>
        </FormControl>
        {renderActionConfig(item.data as AutomationAction)}
      </>;
    } else {
      return renderModifierConfig(item.data as AutomationModifier);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: 'min(600px, calc(100vw - 32px))', minWidth: 0, maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        {optionsError && <Alert color="warning">{optionsError}</Alert>}
        <Typography level="h4" sx={{ mb: 2 }}>
          Configure: {getTitle()}
        </Typography>

        <Stack spacing={2}>
          {renderConfigForm()}
        </Stack>

        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button startDecorator={<Save />} onClick={handleSave}>
            Save Configuration
          </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default WorkflowItemConfigModal;
